import React, { useReducer, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Chip,
  TextField,
  Divider,
} from '@mui/material';
import { Info as InfoIcon, CheckCircle as ResolvedIcon } from '@mui/icons-material';
import ModalHeader from '../../../shared/components/ModalHeader';
import FormActionBar from '../../../shared/components/FormActionBar';
import CopyIconButton from '../../../shared/components/CopyIconButton';
import { safeFormatDate } from '../../../shared';
import type { InvestigationSummary, InvestigationTokenTotals } from '../../../types';
import {
  agentLifetimeTokens,
  subagentLifetimeTokens,
  totalLifetimeTokens,
} from '../../chat/utils/tokenUsage';
import { useFindingsService } from '../../findings';
import { useKnowledgeService } from '../../knowledge';

const UsageFigure: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Box sx={{ minWidth: 110 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">{hint}</Typography>
    )}
  </Box>
);

/**
 * Lifetime token totals for the investigation.
 *
 * Every bucket is shown rather than one summed figure: cache reads are usually
 * the largest bucket and the cheapest per token, so a single "total tokens"
 * number tells an operator almost nothing about what the investigation cost.
 */
const TokenUsageSection: React.FC<{ totals?: InvestigationTokenTotals | null }> = ({ totals }) => {
  if (!totals) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
          Token usage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No completed agent turns yet
        </Typography>
      </Box>
    );
  }

  const subagentTokens = subagentLifetimeTokens(totals);

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
        Token usage
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <UsageFigure
          label="Total"
          value={totalLifetimeTokens(totals).toLocaleString()}
          hint={`${totals.turn_count ?? 0} ${totals.turn_count === 1 ? 'turn' : 'turns'}`}
        />
        <UsageFigure label="Input (uncached)" value={(totals.input_tokens ?? 0).toLocaleString()} />
        <UsageFigure label="Output" value={(totals.output_tokens ?? 0).toLocaleString()} />
        <UsageFigure label="Cache read" value={(totals.cache_read_tokens ?? 0).toLocaleString()} />
        <UsageFigure label="Cache write" value={(totals.cache_write_tokens ?? 0).toLocaleString()} />
        {subagentTokens > 0 && (
          <UsageFigure
            label="Sub-agents"
            value={subagentTokens.toLocaleString()}
            hint={`${totals.subagent_calls ?? 0} calls`}
          />
        )}
        <UsageFigure label="Agent only" value={agentLifetimeTokens(totals).toLocaleString()} />
        <UsageFigure label="LLM cycles" value={(totals.cycle_count ?? 0).toLocaleString()} />
        {(totals.compacted_messages ?? 0) > 0 && (
          <UsageFigure
            label="Summarised away"
            value={(totals.compacted_messages ?? 0).toLocaleString()}
            hint="messages"
          />
        )}
      </Box>
      {totals.updated_at && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Last turn {safeFormatDate(totals.updated_at)}
        </Typography>
      )}
    </Box>
  );
};

interface InvestigationDetailsDialogProps {
  open: boolean;
  investigation: InvestigationSummary | null;
  onClose: () => void;
  onSave?: (investigationId: string, updates: { title?: string; description?: string }) => Promise<void> | void;
}

interface FormData {
  title: string;
  description: string;
}

type FormAction =
  | { type: 'SET_TITLE'; payload: string }
  | { type: 'SET_DESCRIPTION'; payload: string };

function formReducer(state: FormData, action: FormAction): FormData {
  switch (action.type) {
    case 'SET_TITLE':
      return { ...state, title: action.payload };
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload };
  }
}

const getStatusColor = (state: InvestigationSummary['state']) => {
  switch (state) {
    case 'active': return 'success';
    case 'completed': return 'primary';
    case 'archived': return 'default';
    default: return 'default';
  }
};

interface DetailsBodyProps {
  investigation: InvestigationSummary;
  onClose: () => void;
  onSave?: (investigationId: string, updates: { title?: string; description?: string }) => Promise<void> | void;
}

// Rendered only while the dialog is open (see below), so the auth-backed service
// hooks and the count fetch never run for a closed dialog.
const DetailsBody: React.FC<DetailsBodyProps> = ({ investigation, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, dispatch] = useReducer(formReducer, {
    title: investigation.title,
    description: investigation.description || '',
  });
  const findingsService = useFindingsService();
  const knowledgeService = useKnowledgeService();

  // The list surface returns summaries without findings/knowledge; fetch their
  // counts. Supplementary — while pending/on error the chips show a placeholder
  // rather than a misleading "0".
  const { data: counts } = useQuery({
    queryKey: ['investigationCounts', investigation.customer_id, investigation.id],
    queryFn: async () => {
      const [findings, knowledge] = await Promise.all([
        findingsService.getFindingsForInvestigation(investigation.customer_id, investigation.id),
        knowledgeService.getKnowledgeForInvestigation(investigation.customer_id, investigation.id),
      ]);
      return { findings: findings.length, knowledge: knowledge.length };
    },
  });

  const hasChanges = useMemo(
    () => formData.title !== investigation.title || formData.description !== (investigation.description || ''),
    [investigation, formData]
  );

  const handleSave = useCallback(async () => {
    if (!onSave || !hasChanges) return;

    const updates: { title?: string; description?: string } = {};
    if (formData.title.trim() && formData.title !== investigation.title) {
      updates.title = formData.title.trim();
    }
    if (formData.description !== (investigation.description || '')) {
      updates.description = formData.description;
    }

    if (Object.keys(updates).length > 0) {
      setSaving(true);
      await onSave(investigation.id, updates);
      setSaving(false);
    }
  }, [investigation, onSave, formData, hasChanges]);

  const accountCount = investigation.scope.reduce((total, s) => total + (s.accounts?.length || 0), 0);

  return (
    <>
      <ModalHeader
        icon={InfoIcon}
        title="Investigation Details"
        onClose={onClose}
        disableClose={saving}
      />

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {onSave ? (
            <>
              <TextField
                fullWidth
                label="Title *"
                value={formData.title}
                onChange={(e) => dispatch({ type: 'SET_TITLE', payload: e.target.value })}
                error={!formData.title.trim()}
                helperText={!formData.title.trim() ? 'Title is required' : undefined}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => dispatch({ type: 'SET_DESCRIPTION', payload: e.target.value })}
                multiline
                minRows={2}
                maxRows={6}
                helperText="Brief summary of the investigation purpose"
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Title</Typography>
                <Typography variant="body1">{investigation.title}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Description</Typography>
                <Typography variant="body1" color={investigation.description ? 'text.primary' : 'text.secondary'}>
                  {investigation.description || 'No description provided'}
                </Typography>
              </Box>
            </>
          )}

          {/* Reopening deliberately keeps the stored resolution — there is no UI
              path to re-enter one — so only the state says whether it still
              holds. `completed` is the sole state that asserts it: `archived`
              cannot distinguish an investigation filed away after it was
              resolved from one reopened and then filed away. */}
          {investigation.state === 'completed' && investigation.resolution?.trim() && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                p: 1.5,
                border: 1,
                borderColor: 'success.main',
                borderRadius: 1,
              }}
            >
              <ResolvedIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Resolution</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {investigation.resolution.trim()}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={investigation.state}
              size="small"
              color={getStatusColor(investigation.state) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
              sx={{ textTransform: 'capitalize' }}
            />
            <Chip
              label={`${accountCount} ${accountCount === 1 ? 'Account' : 'Accounts'}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={counts ? `${counts.findings} ${counts.findings === 1 ? 'Finding' : 'Findings'}` : 'Findings…'}
              size="small"
              variant="outlined"
            />
            <Chip
              label={counts ? `${counts.knowledge} Knowledge ${counts.knowledge === 1 ? 'Item' : 'Items'}` : 'Knowledge…'}
              size="small"
              variant="outlined"
            />
            {investigation.playbook && (
              <Chip
                label={investigation.playbook.title}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Created</Typography>
              <Typography variant="body2">{safeFormatDate(investigation.created_at)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Last Updated</Typography>
              <Typography variant="body2">{safeFormatDate(investigation.updated_at)}</Typography>
            </Box>
          </Box>

          <Divider />

          <TokenUsageSection totals={investigation.token_usage_total} />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Investigation ID</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {investigation.id}
              </Typography>
              <CopyIconButton text={investigation.id} tooltip="Copy Investigation ID" />
            </Box>
          </Box>

          {onSave && (
            <FormActionBar
              onSave={handleSave}
              saving={saving}
              saveDisabled={!hasChanges || !formData.title.trim()}
            />
          )}
        </Box>
      </DialogContent>
    </>
  );
};

const InvestigationDetailsDialog: React.FC<InvestigationDetailsDialogProps> = ({
  open,
  investigation,
  onClose,
  onSave,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '80vh' } }}>
    {open && investigation && (
      <DetailsBody key={investigation.id} investigation={investigation} onClose={onClose} onSave={onSave} />
    )}
  </Dialog>
);

export default InvestigationDetailsDialog;
